import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { focusRing } from '@repo/studio-design-system/utilities'
import { ChevronRightIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-right'
import { tw } from 'utils/classnames'

interface OptionRowSharedProps extends Omit<React.ComponentProps<'button'>, 'ref'> {
  /**
   * Omit for rows that open a popover instead of toggling a value, so the row does not claim to be
   * a toggle button — pass `aria-expanded` / `aria-haspopup` instead.
   */
  selected?: boolean
  /** Merge row styles and selection onto one child, such as a router `Link`. */
  asChild?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

interface OptionRowLabelProps extends OptionRowSharedProps {
  iconOnly?: false
}

interface OptionRowIconOnlyProps extends OptionRowSharedProps {
  iconOnly: true
  /** Required because the icon-only layout has no visible text. */
  'aria-label': string
}

export type OptionRowProps = OptionRowLabelProps | OptionRowIconOnlyProps

/**
 * OptionRow — an inline parameter trigger for switching a single option, for use in prompt bars,
 * canvas toolbars, inspector sidebars and export popovers. For a non-popover choice track, use
 * `SegmentedControl` instead.
 *
 * @figma https://www.figma.com/design/wx3OX1hccLxomMLDn6QXDa/%F0%9F%9B%9D-Studio-DS---Playground?node-id=3341-18849
 */
function OptionRowRoot({
  selected,
  iconOnly = false,
  type = 'button',
  asChild,
  className,
  children,
  ...props
}: OptionRowProps) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      aria-pressed={asChild ? undefined : selected}
      {...(asChild ? {} : { type })}
      {...props}
      className={tw(
        optionRowClasses.base,
        iconOnly ? optionRowClasses.iconOnlyLayout : optionRowClasses.labelLayout,
        selected ? optionRowClasses.selected : optionRowClasses.unselected,
        props.disabled && optionRowClasses.disabled,
        className,
      )}
    >
      {children}
    </Comp>
  )
}

/**
 * OptionRow.Chevron — the trailing indicator that signals the row opens a further choice. For rows
 * that confirm rather than expand, compose a check icon instead.
 */
function OptionRowChevron({ className, ...props }: React.ComponentProps<'svg'>) {
  return <ChevronRightIcon {...props} className={tw('size-4 shrink-0', className)} />
}

export const OptionRow = Object.assign(OptionRowRoot, {
  Chevron: OptionRowChevron,
})

const optionRowClasses = {
  base: [
    'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap',
    'rounded-studio-geometry-semantic-radius-sm studio-typography-14',
    'outline-none transition-colors',
    focusRing,
    '[&>svg]:size-4 [&>svg]:shrink-0',
  ].join(' '),
  labelLayout: 'h-8 px-2.5 py-1.5',
  iconOnlyLayout: 'size-8 justify-center p-2',
  unselected: [
    'text-theme_semantic_action_select_tertiary_text-color-default',
    'hover:bg-theme_semantic_action_select_tertiary_background-color-hover',
    'hover:text-theme_semantic_action_select_tertiary_text-color-hover',
    'active:bg-theme_semantic_action_select_tertiary_background-color-active',
    'active:text-theme_semantic_action_select_tertiary_text-color-active',
    'focus-visible:bg-theme_semantic_action_select_tertiary_background-color-focus',
    'focus-visible:text-theme_semantic_action_select_tertiary_text-color-focus',
  ].join(' '),
  selected: [
    'bg-theme_semantic_action_select_tertiary_background-color-open',
    'text-theme_semantic_action_select_tertiary_text-color-open',
  ].join(' '),
  disabled: [
    'cursor-not-allowed bg-transparent',
    'text-theme_semantic_action_select_tertiary_text-color-disabled',
    'hover:bg-transparent hover:text-theme_semantic_action_select_tertiary_text-color-disabled',
  ].join(' '),
}
